import { cx } from 'class-variance-authority';
import { redirect } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import useAddOrderToCart from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useAddOrderToCart';
import BuildHardwareOrder from '@/app/[locale]/product/_components/HardwarePDP/BuildHardwareOrder';
import FaqSection from '@/app/[locale]/product/_components/HardwarePDP/FaqSection';
import HardwareEducationSection from '@/app/[locale]/product/_components/HardwarePDP/HardwareEducationSection';
import MembershipSection from '@/app/[locale]/product/_components/HardwarePDP/MembershipSection';
import HardwarePDPModals from '@/app/[locale]/product/_components/HardwarePDP/Modals';
import PDPTechSpecsSection from '@/app/[locale]/product/_components/HardwarePDP/PDPTechSpecsSection';
import ProductSchema from '@/app/[locale]/product/_components/HardwarePDP/ProductSchema';
import type { PageParams } from '@/app/[locale]/product/rings/oura-ring-4/(wizard)/_types/pageParams';
import { POP_PATHNAME } from '@/app/consts/pathnames';
import useUsaOnly from '@/app/hooks/useUsaOnly';
import type { RingFinish } from '@/app/types/ring';

import getProductSelectData from './_data/getProductSelectData';
import { getRingFinishesData } from './_data/getRingFinishesData';
import useHeroCarouselSlides from './_hooks/useHeroCarouselSlides';
import useOR4PDPPage from './_hooks/useOR4PDPPage';
import getTotalPrice from './_utils/getTotalPrice';

const OR4PDPContent = (params: PageParams) => {
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const t = useTranslations();
  const isUS = useUsaOnly();
  const {
    activeModal,
    carouselRef,
    closeModal,
    currencyCode,
    order,
    currentRoute,
    isCarouselInView,
    product,
    productsData,
    productsQueryError,
    ...rest
  } = useOR4PDPPage({ locale: params.locale });
  const { onAddToCart } = useAddOrderToCart({
    order,
    product,
    setIsAddingToCart,
  });
  const ringFinishesData = getRingFinishesData(productsData, t);
  const productSelectData = getProductSelectData({
    currencyCode,
    order,
    ...rest,
    t,
  });
  const heroCarouselSlides = useHeroCarouselSlides();
  const summaryPrice = getTotalPrice({
    product,
    order,
    ...rest,
  });
  const finish = params.finish as RingFinish;

  if (productsQueryError) {
    redirect(POP_PATHNAME);
  }

  return (
    <BuildHardwareOrder
      carousel={{ slides: heroCarouselSlides }}
      carouselRef={carouselRef}
      containerProps={{
        className: 'bg-sandstone-200 md:pt-24',
        name: 'or4-pdp',
        padding: 'none',
      }}
      finishSelectionsProps={{
        currencyCode,
        currentRoute,
        initialSelectedFinish: finish,
        ringFinishesData,
        subtitle: 'ring_pdp_2025_hero_subtitle',
        title: 'oura_ring_4',
      }}
      footerProps={{ wrapperClassName: 'bg-gray-450' }}
      headerProps={{
        headerPosition: 'fixed',
        isCarouselInView,
      }}
      order={order}
      pageName="Rings-OuraRing4"
      productSelectData={productSelectData}
      summaryProps={{
        currentRoute,
        isAddingToCart,
        onAddToCart,
        product,
        productName: 'oura_ring_4',
        subtitle: 'ring_pdp_2025_selected_ring_subtitle',
        summaryPrice,
        tag: isUS && (
          <div
            className={cx(
              'mb-4 inline-block rounded-md bg-gray-300 px-3 py-1.5',
              'text-xs font-bold tracking-wider',
              'text-sandstone-500 uppercase',
            )}
            data-testid="pdp-tag"
          >
            {t('ring_pdp_2025_membership_annual_bullet_3')}
          </div>
        ),
        title: 'ring_pdp_2025_selected_ring_title',
      }}
      {...rest}
    >
      <ProductSchema product={product} />
      <HardwareEducationSection />
      <MembershipSection />
      <FaqSection />
      <PDPTechSpecsSection />
      <HardwarePDPModals activeModal={activeModal} closeModal={closeModal} />
    </BuildHardwareOrder>
  );
};

export default OR4PDPContent;
